<script setup lang="ts">
/**
 * The app's windows (lib/windows.ts) and their taskbar. Mounted by the tmux
 * view, which draws every kind (bar sessions on a phone, where one is a sheet),
 * and by the shell everywhere else, which draws only Global settings (`kinds`):
 * a session, Chat or Diff waits for the tmux view.
 *
 * On a desktop each is a floating window: dragged by its header strip (the
 * Chat's and the Diff's own `.diff-summary`, a session's `.win-drag` bar, so
 * there is no second title bar),
 * resized by its corner, maximised by its ▢ (`WinMaxButton`) or a double-click
 * on that strip, brought to the front by a press anywhere in it. None
 * is modal: the git panel and the terminal stay in use under them. The taskbar
 * along the bottom, once there are two (or the one is put away), lists them all — a click brings one up, or puts away the one
 * in front.
 *
 * On a phone (≤700px) the window in front fills the screen above the taskbar,
 * which is how the others are reached; putting it away shows the view again.
 *
 * What a window holds is the parent's: the default slot is drawn once per
 * window, given it — except Global settings, which is the same wherever it is
 * drawn, so the layer draws it itself.
 */
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import {
  MIN_H,
  MIN_W,
  closeWindow,
  focusWindow,
  frontOf,
  minimizeWindow,
  moveWindow,
  refit,
  setTaskbar,
  taskbarClick,
  toggleMaximize,
  windowTitle,
  windows as allWindows,
  type Win,
  type WindowKind,
} from "../lib/windows.js";
import GlobalSettingsView from "./GlobalSettingsView.vue";

const props = defineProps<{
  /** Put the whole layer away for a moment (the file editor is up). */
  hidden?: boolean;
  /** A better name for a window's tab than its tree or commit — a Chat's task's
   *  title — where the parent knows one. */
  label?: (w: Win) => string | null;
  /** The kinds this layer draws; the rest are neither drawn nor in its taskbar. All when absent. */
  kinds?: readonly WindowKind[];
}>();
defineSlots<{ default(props: { win: Win }): unknown }>();

const NARROW = window.matchMedia("(max-width: 700px)");
const narrow = ref(NARROW.matches);
const onNarrow = (ev: MediaQueryListEvent): void => void (narrow.value = ev.matches);

const windows = computed(() => (props.kinds ? allWindows.value.filter((w) => props.kinds!.includes(w.data.kind)) : allWindows.value));
const frontKey = computed(() => frontOf(windows.value));
const any = computed(() => windows.value.length > 0);
/** Only once there is something to switch between: two or more windows, or the
 *  one put away — the taskbar is then the only way back to it. */
const taskbar = computed(() => windows.value.length > 1 || windows.value.some((w) => w.min));

// The shell leaves the taskbar's height free at its foot, as it does the meters' at its top.
const layer = Symbol("window layer");
watch(taskbar, (on) => setTaskbar(layer, on), { immediate: true });

onMounted(() => {
  NARROW.addEventListener("change", onNarrow);
  window.addEventListener("resize", refit);
});
onBeforeUnmount(() => {
  NARROW.removeEventListener("change", onNarrow);
  window.removeEventListener("resize", refit);
  setTaskbar(layer, false);
});

function shown(w: Win): boolean {
  return !w.min && (!narrow.value || w.key === frontKey.value);
}

function frameStyle(w: Win): Record<string, string> | undefined {
  if (narrow.value) return undefined;
  if (w.max) return { zIndex: String(w.z) };
  const g = w.geom;
  return { left: `${g.x}px`, top: `${g.y}px`, width: `${g.w}px`, height: `${g.h}px`, zIndex: String(w.z) };
}

const INTERACTIVE = "button, select, input, textarea, a, label, [contenteditable], .chip, .status-menu";

/** A press in a window brings it up; one on its header strip (not a control on it) drags it. */
function onPointerDown(w: Win, ev: PointerEvent): void {
  focusWindow(w.key);
  if (narrow.value || ev.button !== 0) return;
  const target = ev.target as HTMLElement;
  if (!onTitleBar(target)) return;
  ev.preventDefault();
  // A maximised one stays put: its geom is where it goes back to.
  if (w.max) return;
  drag(ev, (dx, dy, g) => ({ ...g, x: g.x + dx, y: g.y + dy }), w);
}

function onTitleBar(target: HTMLElement): boolean {
  return !!target.closest(".diff-summary, .win-drag") && !target.closest(INTERACTIVE);
}

/** A double-click on its header strip maximises it, or puts it back. */
function onDblClick(w: Win, ev: MouseEvent): void {
  if (narrow.value || !onTitleBar(ev.target as HTMLElement)) return;
  toggleMaximize(w.key);
}

function onResizeDown(w: Win, ev: PointerEvent): void {
  if (ev.button !== 0) return;
  ev.preventDefault();
  ev.stopPropagation();
  focusWindow(w.key);
  drag(ev, (dx, dy, g) => ({ ...g, w: Math.max(MIN_W, g.w + dx), h: Math.max(MIN_H, g.h + dy) }), w);
}

function drag(ev: PointerEvent, next: (dx: number, dy: number, g: Win["geom"]) => Win["geom"], w: Win): void {
  const startX = ev.clientX;
  const startY = ev.clientY;
  const start = { ...w.geom };
  const move = (e: PointerEvent): void => moveWindow(w.key, next(e.clientX - startX, e.clientY - startY, start));
  const up = (): void => {
    window.removeEventListener("pointermove", move);
    window.removeEventListener("pointerup", up);
    window.removeEventListener("pointercancel", up);
  };
  window.addEventListener("pointermove", move);
  window.addEventListener("pointerup", up);
  window.addEventListener("pointercancel", up);
}

/** Esc puts the window away rather than closing it: what was in its prompt box is kept.
 *  Not a terminal's: Esc there is a key the session needs. */
function onKeydown(w: Win, ev: KeyboardEvent): void {
  if (ev.key !== "Escape" || w.data.kind === "session") return;
  ev.stopPropagation();
  minimizeWindow(w.key);
}

/** The tab's name: the task's title where there is one, else the tree or the commit. */
function tabTitle(w: Win): string {
  return props.label?.(w) || windowTitle(w.data);
}

/** Its tooltip names the tree as well, when the tab shows the task instead. */
function tabHint(w: Win): string {
  const title = tabTitle(w);
  const where = windowTitle(w.data);
  const name = title === where ? title : `${title} (${where})`;
  return w.min ? `Show ${name}` : w.key === frontKey.value ? `Put ${name} away` : `Bring ${name} to the front`;
}

const ICONS: Record<WindowKind, string> = { session: "⌨", chat: "💬", diff: "±", settings: "⚙" };
const KIND_CLASS: Record<WindowKind, string> = { session: "tmux-win", chat: "chat-modal", diff: "diff-win", settings: "settings-win" };

function icon(w: Win): string {
  return ICONS[w.data.kind];
}
</script>

<template>
  <Teleport to="body">
    <template v-if="any">
      <div v-show="!hidden" class="win-layer" :class="{ narrow }">
        <div
          v-for="w in windows"
          v-show="shown(w)"
          :key="w.key"
          class="win"
          :class="[KIND_CLASS[w.data.kind], { front: w.key === frontKey, max: w.max }]"
          role="dialog"
          :aria-label="tabTitle(w)"
          :style="frameStyle(w)"
          @pointerdown="onPointerDown(w, $event)"
          @dblclick="onDblClick(w, $event)"
          @keydown="onKeydown(w, $event)"
        >
          <GlobalSettingsView v-if="w.data.kind === 'settings'" :win-key="w.key" />
          <slot v-else :win="w"></slot>
          <div v-if="!narrow && !w.max" class="win-resize" title="Drag to resize" @pointerdown="onResizeDown(w, $event)"></div>
        </div>
      </div>

      <nav v-if="taskbar" v-show="!hidden" class="win-taskbar" aria-label="Open windows">
        <div
          v-for="w in windows"
          :key="w.key"
          class="win-tab"
          :class="{ front: w.key === frontKey, min: w.min }"
        >
          <button
            class="win-tab-main"
            type="button"
            :title="tabHint(w)"
            :aria-pressed="w.key === frontKey"
            @click="taskbarClick(w.key, frontKey)"
          >
            <span class="win-tab-icon" aria-hidden="true">{{ icon(w) }}</span>
            <span class="win-tab-title">{{ tabTitle(w) }}</span>
          </button>
          <!-- Not on a phone: a tab is too narrow to hit one and not the other. The window's own × closes it. -->
          <button v-if="!narrow" class="win-tab-close" type="button" :title="`Close ${tabTitle(w)}`" aria-label="Close" @click="closeWindow(w.key)">×</button>
        </div>
      </nav>
    </template>
  </Teleport>
</template>
